/**
 * ContactTriggerClient.js
 * ------------------------
 * Client-side implementation of the ContactTrigger system with essential error tracking.
 * Manages initialization, click delegation, modal handling, and fallback behavior
 * for contact buttons across the browser environment.
 *
 * Responsibilities:
 * - Set up global contact button listeners via GlobalContactListener
 * - Delegate button clicks to the ClickHandler for modal processing
 * - Handle fallback behavior using FallbackHandler if modal fails
 * - Maintain singleton behavior (only one instance at a time)
 * - Provide introspection utilities for debugging and diagnostics
 * - Support reinitialization and manual triggering for robustness
 * - Track critical contact trigger system failures with essential error monitoring
 *
 * Features:
 * - Safe DOM readiness handling (`DOMContentLoaded` detection)
 * - Delegated click tracking, validation, and modal launching
 * - Fallback to injected contact form or redirect if modal fails
 * - Global listener state tracking and button interaction logging
 * - Utility methods for full status reporting and lifecycle debugging
 * - Support for reinitialization without full teardown
 * - Essential error tracking for critical contact system failures
 * - Basic debugging capabilities for troubleshooting production issues
 *
 * Error Handling:
 * - Uses ErrorHandler for essential contact trigger failure tracking
 * - Categorizes critical errors: 'initialization', 'click-processing', 'fallback'
 * - Tracks only the most important failures to avoid code bloat
 * - Provides basic error access methods for debugging
 * - Distinguishes between recoverable issues and critical trigger system failures
 *
 * Key Methods:
 * - `init()`                   : Initializes the trigger system if not already initialized
 * - `setupTriggers()`         : Attaches listeners and detects contact buttons
 * - `processContactClick()`   : Responds to a single contact button interaction
 * - `triggerContact()`        : Simulates a user click for testing
 * - `reinitialize()`          : Resets and reinitializes without global destruction
 * - `destroy()`               : Full cleanup of the trigger system and listeners
 * - `getFullStatus()`         : Returns complete debug and health information
 * - `getRecentErrors()`       : Returns recent critical errors for debugging
 * - `hasCriticalIssues()`     : Quick check for fatal trigger system errors
 *
 * Static Utilities:
 * - `hasGlobalInstance()`     : Checks if a global instance is registered
 * - `getGlobalInstance()`     : Returns the current global instance
 * - `forceCleanup()`          : Destroys all internal and global state (emergency use)
 * - `getGlobalDebugInfo()`    : Dumps diagnostics about the global instance and listener
 *
 * Limitations:
 * - Only one global instance is allowed; new instantiation replaces the previous one
 * - Not SSR-compatible — assumes `document`, `window`, and other browser APIs
 * - Depends on `GlobalContactListener`, `ClickHandler`, and `FallbackHandler` functioning correctly
 * - Fallback relies on DOM injection, which may not be CSP-safe
 * - Error tracking is minimal to avoid code explosion (only critical failures)
 * - No detailed trigger analytics or performance monitoring
 */

import DebugHelper from '../utils/DebugHelper.js';
import ClickHandler from '../handlers/ClickHandler.js';
import FallbackHandler from '../handlers/FallbackHandler.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

// Import the GlobalContactListener - assuming it's in the same directory
import globalContactListener from '../handlers/GlobalContactListener.js';

export default class ContactTriggerClient {
    constructor(namespace) {
        // Initialize minimal error handler
        this.errorHandler = new ErrorHandler({
            module: 'ContactTriggerClient',
            maxErrors: 20
        });

        // Check if there's already a global instance
        if (ContactTriggerClient._globalInstance) {
            DebugHelper.warn('ContactTrigger instance already exists globally. Destroying old instance...');
            
            // Track replacement of existing instance
            this.errorHandler.logSuccess('Replacing existing global ContactTrigger instance', {
                category: 'singleton-management',
                action: 'replace-instance'
            });
            
            ContactTriggerClient._globalInstance.destroy();
        }

        this.namespace = namespace;
        this.initialized = false;
        this.clickHandler = null;
        
        // Set this as the global instance
        ContactTriggerClient._globalInstance = this;
        
        this.init();
    }

    /**
     * Initialize the contact trigger system
     * @returns {ContactTriggerClient} This instance for chaining
     */
    init() {
        try {
            if (this.initialized) {
                DebugHelper.warn('ContactTrigger already initialized');
                return this;
            }

            // Validate namespace before proceeding
            if (!this.namespace) {
                throw new Error('Namespace is required for ContactTriggerClient initialization');
            }

            // Initialize click handler
            this.clickHandler = new ClickHandler(this.namespace);
            if (!this.clickHandler) {
                throw new Error('Failed to create ClickHandler instance');
            }

            // Wait for DOM to be fully loaded
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.setupTriggers());
            } else {
                this.setupTriggers();
            }
            
            this.initialized = true;
            DebugHelper.success('ContactTrigger instance created and initialized');
            
            // Track successful initialization
            this.errorHandler.logSuccess('ContactTriggerClient initialized successfully', {
                category: 'initialization',
                action: 'success'
            });
            
            return this;
        } catch (error) {
            this.errorHandler.handleFatalError('ContactTriggerClient initialization failed', error, {
                category: 'initialization',
                action: 'failed',
                hasNamespace: !!this.namespace
            });
            
            DebugHelper.error('ContactTrigger initialization failed:', error);
            throw error;
        }
    }

    /**
     * Setup contact button event listeners
     */
    setupTriggers() {
        try {
            DebugHelper.debug('Setting up contact triggers...');
            
            // Use GlobalContactListener to handle all click events
            globalContactListener.setupListener((button) => this.processContactClick(button));
            
            // Find and log contact buttons for debugging
            const contactButtons = document.querySelectorAll('.contact-us-btn, [data-contact-trigger]');
            DebugHelper.success(`Contact trigger setup complete. Found ${contactButtons.length} button(s)`);
            
            // Log button details for debugging
            DebugHelper.logButtonDetails(contactButtons);
            
            // Track successful trigger setup
            this.errorHandler.logSuccess('Contact triggers setup successfully', {
                category: 'initialization',
                action: 'triggers-setup',
                buttonCount: contactButtons.length
            });
            
        } catch (error) {
            this.errorHandler.handleError('Failed to setup contact triggers', error, {
                category: 'initialization',
                action: 'triggers-setup-failed'
            });
            
            DebugHelper.error('Failed to setup contact triggers:', error);
        }
    }

    /**
     * Process a contact button click
     * @param {HTMLElement} button The clicked button element
     */
    processContactClick(button) {
        try {
            if (!this.clickHandler) {
                throw new Error('Click handler not initialized');
            }

            this.clickHandler.processClick(button);
            
            // Track successful click processing
            this.errorHandler.logSuccess('Contact button click processed', {
                category: 'click-processing',
                action: 'success',
                buttonId: button?.id || 'unknown'
            });
            
        } catch (error) {
            this.errorHandler.handleError('Failed to process contact click', error, {
                category: 'click-processing',
                action: 'failed',
                buttonId: button?.id || 'unknown',
                hasClickHandler: !!this.clickHandler
            });
            
            DebugHelper.error('Click handler not initialized');
            FallbackHandler.handle({ trigger: 'click-processing-failed' });
        }
    }

    /**
     * Handle fallback when modal fails
     */
    handleFallback() {
        try {
            FallbackHandler.handle({ trigger: 'manual-fallback' });
            
            // Track fallback usage
            this.errorHandler.logError('Fallback handler invoked manually', {
                category: 'fallback',
                action: 'manual-invoke'
            });
            
        } catch (error) {
            this.errorHandler.handleError('Failed to invoke fallback handler', error, {
                category: 'fallback',
                action: 'invoke-failed'
            });
        }
    }

    /**
     * Create fallback form (delegated to FallbackHandler)
     * @returns {boolean} True if form was created
     */
    createFallbackForm() {
        try {
            const result = FallbackHandler.createFallbackForm({ trigger: 'manual-fallback-form' });
            
            // Track fallback form creation
            this.errorHandler.logError('Fallback form creation attempted', {
                category: 'fallback',
                action: 'form-creation',
                success: result
            });
            
            return result;
        } catch (error) {
            this.errorHandler.handleError('Failed to create fallback form', error, {
                category: 'fallback',
                action: 'form-creation-failed'
            });
            return false;
        }
    }

    /**
     * Attempt redirect (delegated to FallbackHandler)
     */
    attemptRedirect() {
        try {
            FallbackHandler.attemptRedirect({ trigger: 'manual-redirect' });
            
            // Track redirect attempt
            this.errorHandler.logError('Fallback redirect attempted', {
                category: 'fallback',
                action: 'redirect'
            });
            
        } catch (error) {
            this.errorHandler.handleError('Failed to attempt redirect', error, {
                category: 'fallback',
                action: 'redirect-failed'
            });
        }
    }

    /**
     * Destroy the contact trigger instance
     */
    destroy() {
        try {
            DebugHelper.debug('Destroying ContactTrigger...');
            
            // Remove the global listener
            globalContactListener.removeListener();
            
            // Only reset global instance if this is the global instance
            if (ContactTriggerClient._globalInstance === this) {
                ContactTriggerClient._globalInstance = null;
            }
            
            // Cleanup click handler
            this.clickHandler = null;
            this.initialized = false;
            
            // Clean up fallback forms
            FallbackHandler.cleanup();
            
            DebugHelper.success('ContactTrigger destroyed');
            
            // Track successful destruction
            this.errorHandler.logSuccess('ContactTriggerClient destroyed successfully', {
                category: 'lifecycle',
                action: 'destroy-success'
            });
            
        } catch (error) {
            this.errorHandler.handleError('Error during ContactTriggerClient destruction', error, {
                category: 'lifecycle',
                action: 'destroy-failed'
            });
            
            DebugHelper.error('Error destroying ContactTrigger:', error);
        }
    }

    /**
     * Manually trigger contact modal (for testing/debugging)
     */
    triggerContact() {
        try {
            DebugHelper.debug('Manual contact trigger');
            
            // Reset the global listener processing state
            globalContactListener.reset();
            
            if (!this.clickHandler) {
                throw new Error('Click handler not available for manual trigger');
            }

            this.clickHandler.processManualTrigger();
            
            // Track manual trigger
            this.errorHandler.logSuccess('Manual contact trigger executed', {
                category: 'manual-trigger',
                action: 'success'
            });
            
        } catch (error) {
            this.errorHandler.handleError('Manual contact trigger failed', error, {
                category: 'manual-trigger',
                action: 'failed',
                hasClickHandler: !!this.clickHandler
            });
            
            DebugHelper.error('Click handler not available for manual trigger');
        }
    }

    /**
     * Get debug information for this instance
     * @returns {Object} Debug information
     */
    getDebugInfo() {
        return DebugHelper.getInstanceDebugInfo(this, globalContactListener);
    }

    /**
     * Get initialization status
     * @returns {boolean} True if initialized
     */
    isInitialized() {
        return this.initialized;
    }

    /**
     * Get namespace status
     * @returns {Object} Namespace information
     */
    getNamespaceStatus() {
        return {
            hasNamespace: !!this.namespace,
            namespaceType: this.namespace ? typeof this.namespace : null,
            health: this.namespace && this.namespace.healthCheck ? 
                    this.namespace.healthCheck() : null
        };
    }

    /**
     * Get click handler status
     * @returns {Object} Click handler information
     */
    getClickHandlerStatus() {
        return {
            hasClickHandler: !!this.clickHandler,
            clickHandlerStats: this.clickHandler ? this.clickHandler.getStats() : null
        };
    }

    /**
     * Get comprehensive status of the contact trigger
     * @returns {Object} Complete status information
     */
    getFullStatus() {
        const errorStats = this.errorHandler.getErrorStats();
        
        return {
            initialized: this.initialized,
            isGlobalInstance: ContactTriggerClient._globalInstance === this,
            namespace: this.getNamespaceStatus(),
            clickHandler: this.getClickHandlerStatus(),
            globalListener: {
                active: globalContactListener.isActive(),
                processing: globalContactListener.getProcessingState()
            },
            errors: {
                total: errorStats.totalErrors,
                fatal: errorStats.fatalErrors,
                recent: errorStats.recentErrors,
                byCategory: errorStats.errorsByCategory
            },
            debug: this.getDebugInfo(),
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Reinitialize the contact trigger system
     */
    reinitialize() {
        try {
            DebugHelper.info('Reinitializing ContactTrigger...');
            
            const wasInitialized = this.initialized;
            
            if (wasInitialized) {
                // Don't destroy completely, just reset components
                globalContactListener.removeListener();
                this.initialized = false;
            }

            this.init();
            
            DebugHelper.success('ContactTrigger reinitialized');
            
            // Track successful reinitialization
            this.errorHandler.logSuccess('ContactTriggerClient reinitialized successfully', {
                category: 'lifecycle',
                action: 'reinitialize-success',
                wasInitialized
            });
            
        } catch (error) {
            this.errorHandler.handleError('ContactTriggerClient reinitialization failed', error, {
                category: 'lifecycle',
                action: 'reinitialize-failed'
            });
            
            DebugHelper.error('Failed to reinitialize ContactTrigger:', error);
        }
    }

    /**
     * Get recent errors for debugging
     * @param {number} minutes Number of minutes to look back
     * @returns {Array} Recent error objects
     */
    getRecentErrors(minutes = 5) {
        return this.errorHandler.getRecentErrors(minutes);
    }

    /**
     * Check if there are critical issues
     * @returns {boolean} True if fatal errors exist
     */
    hasCriticalIssues() {
        return this.errorHandler.hasFatalErrors();
    }

    /**
     * Clear error history
     */
    clearErrorHistory() {
        this.errorHandler.clearErrors();
    }

    /**
     * Static method to get debug info about global state
     * @returns {Object} Global debug information
     */
    static getGlobalDebugInfo() {
        return DebugHelper.getGlobalDebugInfo(ContactTriggerClient, globalContactListener);
    }

    /**
     * Static method to force cleanup (emergency use)
     */
    static forceCleanup() {
        DebugHelper.warn('Force cleaning up all ContactTrigger instances...');
        
        try {
            if (ContactTriggerClient._globalInstance) {
                ContactTriggerClient._globalInstance.destroy();
            }
            
            ContactTriggerClient._globalInstance = null;
            globalContactListener.destroy();
            
            DebugHelper.success('Force cleanup complete');
        } catch (error) {
            console.error('Error during force cleanup:', error);
        }
    }

    /**
     * Static method to check if global instance exists
     * @returns {boolean} True if global instance exists
     */
    static hasGlobalInstance() {
        return !!ContactTriggerClient._globalInstance;
    }

    /**
     * Static method to get the global instance
     * @returns {ContactTriggerClient|null} Global instance or null
     */
    static getGlobalInstance() {
        return ContactTriggerClient._globalInstance;
    }
}

// Global state tracking for client-side
ContactTriggerClient._globalInstance = null;
